import React, { useState, useEffect, useRef, useMemo } from 'react';
import proptypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import {
  MenuItem,
  TextField,
  Accordion,
  AccordionHeader,
  AccordionBody,
  Typography
} from '@one-thd/sui-atomic-components';
import {
  validateField,
  validateEstimatedStartDate,
  validateEstimatedEndDate
} from '../helpers';
import {
  PROJECT_NAME_KEY,
  PROJECT_TYPE_KEY,
  ESTIMATED_START_DATE_KEY,
  ESTIMATED_END_DATE_KEY,
  JOB_SITE_CONDITIONS_KEY,
  DELIVERY_INSTRUCTIONS_KEY,
  DELIVERY_CONTACT_NAME_KEY,
  DELIVERY_CONTACT_PHONE_KEY,
  SECURITY_CODE,
  LOCKBOX_CODE,
  LOCKBOX_LOCATION,
  JOB_SITE_HOURS_START,
  JOB_SITE_HOURS_END,
  PROPERTY_TYPE_KEY,
  PROPERTY_TYPE_OTHER_KEY,
  PACKAGE_PLACEMENT_KEY,
  DELIVERY_TYPE_KEY,
  HAS_STAIRS_KEY,
  EMPTY_PROJECT_NAME_MESSAGE,
  INVALID_JOB_SITE_HOURS_WINDOW_MESSAGE,
  MISSING_JOB_SITE_HOURS_END_MESSAGE,
  PROJECT_DELIVERY_DETAILS_TITLE_BASE,
  PROJECT_DELIVERY_DETAILS_HELPER_TEXT,
  ADDRESS_FORM_ADDRESS_DETAILS_HELPER_TEXT,
  JOBSITE_DELIVERY_CONTACT_HELPER_TEXT,
  ADDRESS_FORM_DELIVERY_TIME_WINDOW_HELPER_TEXT,
  ADDRESS_FORM_DELIVERY_INSTRUCTIONS_HELPER_TEXT
} from '../constants';
import { JobsiteInformationForm } from './JobsiteInformationForm';
import { DeliveryZipField } from './DeliveryZipField';
import { ValidatedTextField, ValidatedDropdown } from './ValidatedFields';
import {
  ProjectTypeSection,
  EstimatedDatesSection,
  PropertyTypeSection,
  JobSiteHoursSection
} from './ProjectInformationSections';

const { bool, func, string, shape } = proptypes;

// Matches every comp screenshot: a bold section title with grey helper copy underneath it,
// repeated once per section (Address Details, Delivery Contact, Delivery Time Window, Delivery
// Instructions). Missing for three of the four sections in the first comp-exactness pass - that
// pass fixed field labels and order but never added the section wrapper itself. [DSCVR-5168]
const SectionHeading = ({ title, helperText }) => (
  <div>
    <div className="sui-pb-2">
      <Typography variant="body-lg" weight="bold">
        {title}
      </Typography>
    </div>
    {/* DSCVR-5168: dark, not subtle - the comp shows this darker than the helper text above
        the accordion (PROJECT_DELIVERY_DETAILS_HELPER_TEXT), which stays subtle. No bottom
        padding here, unlike renderJobsiteSection's helper text below - SectionHeading and the
        fields that follow it (e.g. deliveryContactNameField) are separate siblings inside the
        sui-grid sui-gap-6 accordion body, so that grid gap alone already supplies the gap to the
        first field. Adding sui-pb-6 here stacked on top of it and doubled the gap. */}
    <Typography variant="body-base" color="primary">
      {helperText}
    </Typography>
  </div>
);

SectionHeading.propTypes = {
  title: string.isRequired,
  helperText: string.isRequired
};

// The accordion's heading and helper copy. Switched off it is the pre-DSCVR-5168 "Project Details
// (Optional)" header, unchanged. [DSCVR-5191]
const AccordionTitle = ({ isJobsiteInformationEnabled }) => (isJobsiteInformationEnabled ? (
  <>
    <Typography variant="h6">
      {PROJECT_DELIVERY_DETAILS_TITLE_BASE}
      {' '}
      <Typography component="span" variant="h6" weight="regular" color="subtle">
        (Optional)
      </Typography>
    </Typography>
    <div className="sui-mt-1">
      <Typography variant="body-base" color="subtle">
        {PROJECT_DELIVERY_DETAILS_HELPER_TEXT}
      </Typography>
    </div>
  </>
) : (
  <>
    <Typography variant="h6">Project Details (Optional)</Typography>
    <div className="sui-mt-1">
      <Typography variant="body-base" color="subtle">
        Adding these details will help us show you accurate inventory and delivery times
      </Typography>
    </div>
  </>
));

AccordionTitle.propTypes = {
  isJobsiteInformationEnabled: bool.isRequired
};

const DEFINED_PROPERTY_TYPES = ['House', 'Apartment', 'Business', 'Construction Site'];

// Field labels: the DSCVR-5168 comp wording only for the switched-on create drawer, the original
// labels everywhere else (Project Setup included). One lookup rather than a ternary per field,
// which keeps renderFormFields inside Sonar's complexity limit. [DSCVR-5191]
const FIELD_LABELS = {
  contactNumber: 'Delivery Contact Number',
  deliveryInstructions: 'Delivery Instructions',
  hasStairs: 'Has Stairs?'
};
const JOBSITE_FIELD_LABELS = {
  contactNumber: 'Phone Number',
  deliveryInstructions: 'Delivery Requirements',
  hasStairs: 'Stairs'
};

export const ProjectInformationForm = ({
  showErrors,
  projectInformation,
  setProjectInformation,
  isProjectInfoError,
  setIsProjectInfoError,
  deliveryPreferences,
  setDeliveryPreferences,
  contacts,
  setContacts,
  open,
  duplicateNameError,
  hasTradeCreditAttached,
  jobsiteInformation,
  setJobsiteInformation,
  isJobsiteError,
  setIsJobsiteError,
  hideDeliveryInfo,
  hideProjectInfo,
  isJobsiteInformationEnabled,
  setIsDeliveryZipError
}) => {
  // All refs
  const projectNameRef = useRef(null);
  const projectTypeRef = useRef(null);
  const estimatedEndDateRef = useRef(null);
  const notesRef = useRef(null);
  const siteConditionsRef = useRef(null);
  const deliveryInstructionsRef = useRef(null);
  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const packagePlacementRef = useRef(null);
  const securityCodeRef = useRef(null);
  const lockboxCodeRef = useRef(null);
  const lockboxLocationRef = useRef(null);
  const propertyTypeRef = useRef(null);
  const propertyTypeOtherRef = useRef(null);
  const deliveryTypeRef = useRef(null);
  const jobSiteHoursStartRef = useRef(null);
  const jobSiteHoursEndRef = useRef(null);
  const hasStairsErrorRef = useRef(null);

  const [errors, setErrors] = useState({
    projectNameError: '',
    propertyTypeError: '',
    propertyTypeOtherError: '',
    estimatedEndDateError: '',
    notesError: '',
    siteConditionsError: '',
    deliveryInstructionsError: '',
    phoneError: '',
    nameError: '',
    preferredPackagePlacementError: '',
    securityCodeError: '',
    lockboxCodeError: '',
    lockboxLocationError: '',
    deliveryTypeError: '',
    jobSiteHoursStartError: '',
    jobSiteHoursEndError: '',
    hasStairsError: ''
  });

  const [expandedAccordion, setExpandedAccordion] = useState(false);

  const {
    projectName = '',
    projectType = null,
    estimatedStartDate = '',
    estimatedEndDate = ''
  } = projectInformation || {};

  const {
    siteConditions = '',
    deliveryInstructions = '',
    preferredPackagePlacement = '',
    securityCode = '',
    lockboxCode = '',
    lockboxLocation = '',
    deliveryType = null,
    propertyType = null,
    propertyTypeOther = '',
    hasStairs = null,
    jobSiteHoursStart = null,
    jobSiteHoursEnd = ''
  } = deliveryPreferences || {};

  const isPropertyTypeOther = !DEFINED_PROPERTY_TYPES.includes(propertyType) && !!propertyType;

  // Normalize legacy custom propertyType values into propertyTypeOther on load
  useEffect(() => {
    if (
      deliveryPreferences?.propertyType
      && !DEFINED_PROPERTY_TYPES.includes(deliveryPreferences.propertyType)
      && deliveryPreferences.propertyType !== 'Other'

    ) {
      setDeliveryPreferences((prev) => ({
        ...prev,
        propertyTypeOther: prev.propertyType,
        propertyType: 'Other'
      }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { name = '', phone = '' } = contacts || {};

  const [propertyTypeDropdownValue, setPropertyTypeDropdownValue] = useState(null);

  const formatHour = (hour) => {
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    return `${displayHour}:00 ${period}`;
  };

  const parseTimeTo24Hour = (timeStr) => {
    if (!timeStr) {
      return null;
    }
    const match = timeStr.match(/^(\d{1,2}):(\d{2})\s?(AM|PM)$/i);
    if (!match) {
      return null;
    }
    let hour = parseInt(match[1], 10);
    const period = match[3].toUpperCase();
    if (period === 'AM' && hour === 12) {
      hour = 0;
    }
    if (period === 'PM' && hour !== 12) {
      hour += 12;
    }
    return hour;
  };

  const startTimeOptions = useMemo(() => {
    return Array.from({ length: 9 }, (_unused, i) => formatHour(6 + i));
  }, []);

  const allEndTimeOptions = useMemo(() => {
    return Array.from({ length: 9 }, (_unused, i) => formatHour(12 + i));
  }, []);

  const endTimeOptions = useMemo(() => {
    const startHour = parseTimeTo24Hour(jobSiteHoursStart);
    if (startHour === null) {
      return allEndTimeOptions;
    }
    const minEndHour = startHour + 6;
    return allEndTimeOptions.filter((time) => {
      const endHour = parseTimeTo24Hour(time);
      return endHour >= minEndHour;
    });
  }, [jobSiteHoursStart, allEndTimeOptions]);
  const getPhoneNumberDisplayValue = (phoneNumber) => {
    if (!phoneNumber) {
      return '';
    }
    const digits = (phoneNumber || '').replace(/\D/g, '');
    if (digits.length === 0) {
      return '';
    }
    if (digits.length <= 3) {
      return `(${digits}`;
    }
    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const getHasStairsDisplayValue = (value) => {
    if (value === true) {
      return 'Yes';
    }
    if (value === false) {
      return 'No';
    }
    return '';
  };

  const createValidationHandler = ({ valueGetter, key, errorKey }) => {
    return () => {
      const value = valueGetter();
      const { isError, errorMessage } = validateField({ value, key });
      setErrors((prevState) => ({
        ...prevState,
        [errorKey]: isError ? errorMessage : ''
      }));
    };
  };

  const handleNameValidation = createValidationHandler({
    valueGetter: () => projectName,
    key: PROJECT_NAME_KEY,
    errorKey: 'projectNameError'
  });

  const handleOtherTypeValidation = createValidationHandler({
    valueGetter: () => projectType,
    key: PROJECT_TYPE_KEY,
    errorKey: 'projectTypeError'
  });

  const handleSiteConditionsValidation = createValidationHandler({
    valueGetter: () => siteConditions,
    key: JOB_SITE_CONDITIONS_KEY,
    errorKey: 'siteConditionsError'
  });

  const handleDeliveryInstructionsValidation = createValidationHandler({
    valueGetter: () => deliveryInstructions,
    key: DELIVERY_INSTRUCTIONS_KEY,
    errorKey: 'deliveryInstructionsError'
  });

  const handleContactNameValidation = createValidationHandler({
    valueGetter: () => name,
    key: DELIVERY_CONTACT_NAME_KEY,
    errorKey: 'nameError'
  });

  const handlePhoneValidation = createValidationHandler({
    valueGetter: () => phone,
    key: DELIVERY_CONTACT_PHONE_KEY,
    errorKey: 'phoneError'
  });

  const handleSecurityCodeValidation = createValidationHandler({
    valueGetter: () => securityCode,
    key: SECURITY_CODE,
    errorKey: 'securityCodeError'
  });

  const handleLockBoxCodeValidation = createValidationHandler({
    valueGetter: () => lockboxCode,
    key: LOCKBOX_CODE,
    errorKey: 'lockboxCodeError'
  });

  const handleLockBoxLocationValidation = createValidationHandler({
    valueGetter: () => lockboxLocation,
    key: LOCKBOX_LOCATION,
    errorKey: 'lockboxLocationError'
  });

  const handlePropertyTypeValidation = createValidationHandler({
    valueGetter: () => propertyType,
    key: PROPERTY_TYPE_KEY,
    errorKey: 'propertyTypeError'
  });

  const handlePropertyTypeOtherValidation = createValidationHandler({
    valueGetter: () => propertyTypeOther,
    key: PROPERTY_TYPE_OTHER_KEY,
    errorKey: 'propertyTypeOtherError'
  });

  const handlePackageValidation = createValidationHandler({
    valueGetter: () => preferredPackagePlacement,
    key: PACKAGE_PLACEMENT_KEY,
    errorKey: 'preferredPackagePlacementError'
  });

  const handleDeliveryTypeValidation = createValidationHandler({
    valueGetter: () => deliveryType,
    key: DELIVERY_TYPE_KEY,
    errorKey: 'deliveryTypeError'
  });

  const handleHasStairsValidation = createValidationHandler({
    valueGetter: () => hasStairs,
    key: HAS_STAIRS_KEY,
    errorKey: 'hasStairsError'
  });

  const validateJobSiteHoursWindow = (start, end) => {
    const startHour = parseTimeTo24Hour(start);
    const endHour = parseTimeTo24Hour(end);
    if (startHour !== null && endHour !== null && (endHour - startHour) < 6) {
      return INVALID_JOB_SITE_HOURS_WINDOW_MESSAGE;
    }
    return '';
  };

  const handleJobSiteHoursStartValidation = () => {
    const windowError = validateJobSiteHoursWindow(jobSiteHoursStart, jobSiteHoursEnd);
    setErrors((prevState) => ({
      ...prevState,
      jobSiteHoursEndError: windowError
    }));
  };

  const handleJobSiteHoursEndValidation = () => {
    const windowError = validateJobSiteHoursWindow(jobSiteHoursStart, jobSiteHoursEnd);
    setErrors((prevState) => ({
      ...prevState,
      jobSiteHoursEndError: windowError
    }));
  };

  useEffect(() => {
    if (jobSiteHoursStart && jobSiteHoursEnd) {
      const windowError = validateJobSiteHoursWindow(jobSiteHoursStart, jobSiteHoursEnd);
      setErrors((prevState) => ({
        ...prevState,
        jobSiteHoursEndError: windowError
      }));
    }
  }, [jobSiteHoursStart, jobSiteHoursEnd]);

  const handleTypeValidation = () => {
    setErrors((prevState) => ({
      ...prevState,
      propertyTypeError: ''
    }));
  };

  const handleChangeAccordion = (_event, isExpanded) => {
    setExpandedAccordion(isExpanded);
  };

  const handleEstimatedStartDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedStartDate(value, estimatedEndDate);
    setErrors((prevState) => ({
      ...prevState,
      estimatedEndDateError: isError ? endDateErrorMessage : ''
    }));
  };

  const handleEstimatedEndDateValidation = (value) => {
    const { isError, endDateErrorMessage } = validateEstimatedEndDate(value, estimatedStartDate);
    setErrors((prevState) => ({
      ...prevState,
      estimatedEndDateError: isError ? endDateErrorMessage : ''
    }));
  };

  const handleOnDateChange = (key, _event, value) => {
    let formattedValue = value;
    if (value) {
      formattedValue = new Date(value).toLocaleDateString(
        'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' }
      );
    }
    if (key === ESTIMATED_START_DATE_KEY) {
      handleEstimatedStartDateValidation(formattedValue);
    }
    if (key === ESTIMATED_END_DATE_KEY) {
      handleEstimatedEndDateValidation(formattedValue);
    }
    setProjectInformation((prevState) => ({
      ...prevState,
      [key]: formattedValue
    }));
  };

  const handleOnChange = (key, event) => {
    const { value } = event.target;
    if (key === DELIVERY_CONTACT_NAME_KEY) {
      setContacts((prevState) => ({
        ...prevState,
        [key]: value
      }));
    } else if (key === DELIVERY_CONTACT_PHONE_KEY) {
      const digits = value.replace(/\D/g, '').slice(0, 10);
      setContacts((prevState) => ({
        ...prevState,
        [key]: digits
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  const handleDeliveryPreferencesOnChange = (key, event) => {
    const { value } = event.target;
    let processedValue = value;
    if (key === HAS_STAIRS_KEY) {
      if (value === 'Yes') {
        processedValue = true;
      } else if (value === 'No') {
        processedValue = false;
      } else {
        processedValue = null;
      }
    }
    if (key === JOB_SITE_HOURS_START && !value) {
      setDeliveryPreferences((prevState) => ({
        ...prevState,
        [JOB_SITE_HOURS_START]: value,
        [JOB_SITE_HOURS_END]: ''
      }));
      setErrors((prevState) => ({
        ...prevState,
        jobSiteHoursEndError: ''
      }));
      return;
    }
    let isPropertyType = key === PROPERTY_TYPE_KEY;
    if (isPropertyType) {
      if (value === 'Other') {
        handlePropertyTypeOtherValidation();
      } else {
        setErrors((prevSate) => ({
          ...prevSate,
          propertyTypeOtherError: ''
        }));
      }
    }
    setDeliveryPreferences((prevState) => ({
      ...prevState,
      [key]: processedValue,
      ...(isPropertyType && value !== 'Other'
        ? { [PROPERTY_TYPE_OTHER_KEY]: '' }
        : {})
    }));
  };

  const handleOnPropertyChange = (key, event) => {
    const { value } = event.target;
    setPropertyTypeDropdownValue(value);
    if (value === 'Other') {
      setProjectInformation((prevState) => ({
        ...prevState,
        projectType: ''
      }));
    } else {
      setProjectInformation((prevState) => ({
        ...prevState,
        [key]: value
      }));
    }
  };

  useEffect(() => {
    const errorMessageValues = Object.values(errors);
    const hasErrors = errorMessageValues.some(Boolean);
    setIsProjectInfoError(hasErrors);
  }, [errors, setIsProjectInfoError]);

  useEffect(() => {
    const findFirstTruthyKey = (obj) => {
      return Object.keys(obj).find((key) => obj[key]) || null;
    };
    const firstErrorKey = findFirstTruthyKey(errors);
    if (showErrors && firstErrorKey) {
      const errorRefMap = {
        projectNameError: projectNameRef,
        projectTypeError: projectTypeRef,
        estimatedEndDateError: estimatedEndDateRef,
        notesError: notesRef,
        siteConditionsError: siteConditionsRef,
        deliveryInstructionsError: deliveryInstructionsRef,
        nameError: nameRef,
        phoneError: phoneRef,
        preferredPackagePlacementError: packagePlacementRef,
        securityCodeError: securityCodeRef,
        lockboxCodeError: lockboxCodeRef,
        lockboxLocationError: lockboxLocationRef,
        deliveryTypeError: deliveryTypeRef,
        jobSiteHoursStartError: jobSiteHoursStartRef,
        jobSiteHoursEndError: jobSiteHoursEndRef,
        propertyTypeError: propertyTypeRef,
        propertyTypeOtherError: propertyTypeOtherRef,
        hasStairsError: hasStairsErrorRef
      };
      const targetRef = errorRefMap[firstErrorKey];
      if (targetRef?.current) {
        targetRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
      }
    }
  }, [showErrors, errors]);

  useEffect(() => {
    if (showErrors && !projectName) {
      setErrors((prevSate) => ({
        ...prevSate,
        projectNameError: EMPTY_PROJECT_NAME_MESSAGE
      }));
    }
    if (showErrors && jobSiteHoursStart && !jobSiteHoursEnd) {
      setErrors((prevState) => ({
        ...prevState,
        jobSiteHoursEndError: MISSING_JOB_SITE_HOURS_END_MESSAGE
      }));
    } else if (showErrors && jobSiteHoursStart && jobSiteHoursEnd) {
      const windowError = validateJobSiteHoursWindow(jobSiteHoursStart, jobSiteHoursEnd);
      setErrors((prevState) => ({
        ...prevState,
        jobSiteHoursEndError: windowError
      }));
    }
  }, [showErrors]);

  useEffect(() => {
    if (projectType) {
      // eslint-disable-next-line max-len
      if (projectType !== 'Single Family' && projectType !== 'Multi-Family' && projectType !== 'Renovation/Remodel') {
        setPropertyTypeDropdownValue('Other');
      } else {
        setPropertyTypeDropdownValue(projectType);
      }
    }
  }, []);

  // Both layouts below render the same fields; each field is declared once here and
  // the two layouts differ only in the order they compose them (Jobsite Conditions and
  // Delivery Type sit in different places) plus the estimated-dates wrapper, which
  // EstimatedDatesSection handles via isAccordion.
  // Identical block either way - only where it sits and the heading spacing differ - so both
  // placements render from here rather than duplicating the JSX.
  // DSCVR-5168 AC3: inside the Project Delivery Details accordion this section is headed
  // "Address Details" and sits first, matching the comps and the Project Setup tab. The legacy
  // (switch-off) layout keeps "Jobsite Information".
  // Typography silently discards a custom className (it builds its own from
  // variant/weight/color - confirmed in its source), so headingClassName never actually applied
  // here either, for both this call's Address Details and the legacy Jobsite Information call.
  // Wrapping divs instead, matching the pattern already used elsewhere in this file for the
  // outer "Project Delivery Details" helper text.
  // Switched off, the heading is the pre-5168 markup, unchanged. [DSCVR-5191]
  const renderJobsiteSection = (headingClassName, title = 'Jobsite Information', helperText = null) => (
    <div>
      {isJobsiteInformationEnabled ? (
        <div className={headingClassName}>
          <Typography variant="body-lg" weight="bold">
            {title}
          </Typography>
        </div>
      ) : (
        <Typography variant="body-lg" weight="bold" className={headingClassName}>
          {title}
        </Typography>
      )}
      {helperText && (
        <div className="sui-pb-6">
          <Typography variant="body-base" color="primary">
            {helperText}
          </Typography>
        </div>
      )}
      <JobsiteInformationForm
        showErrors={showErrors}
        jobsiteInformation={jobsiteInformation}
        setJobsiteInformation={setJobsiteInformation}
        isJobsiteError={isJobsiteError}
        setIsJobsiteError={setIsJobsiteError}
        open={open}
        isZipOnlyEnabled={isJobsiteInformationEnabled}
        isJobsiteInformationEnabled={isJobsiteInformationEnabled}
      />
    </div>
  );

  const fieldLabels = isJobsiteInformationEnabled ? JOBSITE_FIELD_LABELS : FIELD_LABELS;

  const renderFormFields = (isAccordion = false) => {
    const idSuffix = isAccordion ? '-accordion' : '';
    const testIdSuffix = isAccordion ? '-accordion' : '';

    const projectTypeSection = (
      <ProjectTypeSection
        hidden={hideProjectInfo}
        idSuffix={idSuffix}
        dropdownValue={propertyTypeDropdownValue}
        onDropdownBlur={() => { handleTypeValidation(); }}
        onDropdownChange={(event) => { handleOnPropertyChange(PROJECT_TYPE_KEY, event); }}
        projectType={projectType}
        projectTypeRef={projectTypeRef}
        onProjectTypeBlur={handleOtherTypeValidation}
        onProjectTypeChange={(event) => { handleOnChange(PROJECT_TYPE_KEY, event); }}
        error={errors.projectTypeError}
        showErrors={showErrors}
      />
    );

    const estimatedDates = (
      <EstimatedDatesSection
        hidden={hideProjectInfo}
        isAccordion={isAccordion}
        estimatedStartDate={estimatedStartDate}
        estimatedEndDate={estimatedEndDate}
        estimatedEndDateRef={estimatedEndDateRef}
        onStartDateChange={(event, value) => { handleOnDateChange(ESTIMATED_START_DATE_KEY, event, value); }}
        onEndDateChange={(event, value) => { handleOnDateChange(ESTIMATED_END_DATE_KEY, event, value); }}
        endDateError={errors.estimatedEndDateError}
        showErrors={showErrors}
      />
    );

    const jobsiteConditionsField = (
      <ValidatedTextField
        hidden={hideDeliveryInfo}
        ref={siteConditionsRef}
        optional
        id="jobsite-conditions"
        label="Jobsite Conditions"
        value={siteConditions}
        onBlur={handleSiteConditionsValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_CONDITIONS_KEY, event); }}
        error={errors.siteConditionsError}
        showErrors={showErrors}
        testId="jobsite-conditions-input"
      />
    );

    const deliveryContactNameField = (
      <ValidatedTextField
        hidden={hideProjectInfo}
        ref={nameRef}
        optional
        id="delivery-contact-name"
        label="Delivery Contact Name"
        value={name}
        onBlur={handleContactNameValidation}
        onChange={(event) => { handleOnChange('name', event); }}
        error={errors.nameError}
        showErrors={showErrors}
        testId="delivery-contact-name-input"
      />
    );

    const deliveryContactNumberField = (
      <ValidatedTextField
        hidden={hideProjectInfo}
        ref={phoneRef}
        optional
        id="delivery-contact-number"
        label={fieldLabels.contactNumber}
        placeholder="(555) 555-5555"
        value={getPhoneNumberDisplayValue(phone)}
        onBlur={handlePhoneValidation}
        onChange={(event) => { handleOnChange('phone', event); }}
        error={errors.phoneError}
        showErrors={showErrors}
        testId="delivery-contact-number-input"
      />
    );

    const deliveryInstructionsField = (
      <ValidatedTextField
        hidden={hideDeliveryInfo}
        ref={deliveryInstructionsRef}
        optional
        id="delivery-instructions"
        label={fieldLabels.deliveryInstructions}
        value={deliveryInstructions}
        onBlur={handleDeliveryInstructionsValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_INSTRUCTIONS_KEY, event); }}
        error={errors.deliveryInstructionsError}
        showErrors={showErrors}
        testId="delivery-instructions-input"
      />
    );

    const deliveryTypeField = (
      <ValidatedDropdown
        hidden={hideDeliveryInfo}
        ref={deliveryTypeRef}
        id={`delivery-type${idSuffix}`}
        testId={`delivery-type${testIdSuffix}-dropdown`}
        label="Delivery Type"
        value={deliveryType ?? null}
        onBlur={handleDeliveryTypeValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(DELIVERY_TYPE_KEY, event); }}
        placeholder="Select a delivery type"
        error={errors.deliveryTypeError}
        showErrors={showErrors}
      >
        <MenuItem value="" data-testid={`delivery${testIdSuffix}-none`}>No Preference</MenuItem>
        <MenuItem value="Outside" data-testid={`delivery${testIdSuffix}-outside`}>Outside</MenuItem>
        <MenuItem value="Threshold" data-testid={`delivery${testIdSuffix}-threshold`}>Threshold</MenuItem>
        <MenuItem value="Lockbox" data-testid={`delivery${testIdSuffix}-lockbox`}>Lockbox</MenuItem>
      </ValidatedDropdown>
    );

    const packagePlacementField = (
      <ValidatedDropdown
        hidden={hideDeliveryInfo}
        ref={packagePlacementRef}
        id={`package-placement${idSuffix}`}
        testId={`package-placement${testIdSuffix}-dropdown`}
        label="Package Placement"
        value={preferredPackagePlacement || ''}
        onBlur={handlePackageValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(PACKAGE_PLACEMENT_KEY, event); }}
        placeholder="Select a placement option"
        error={errors.preferredPackagePlacementError}
        showErrors={showErrors}
      >
        <MenuItem value="No Preference" data-testid={`package${testIdSuffix}-no-preference`}>
          No Preference
        </MenuItem>
        <MenuItem value="Front Door" data-testid={`package${testIdSuffix}-front-door`}>Front Door</MenuItem>
        <MenuItem value="Back Door" data-testid={`package${testIdSuffix}-back-door`}>Back Door</MenuItem>
        <MenuItem value="Porch" data-testid={`package${testIdSuffix}-porch`}>Porch</MenuItem>
        <MenuItem value="Office" data-testid={`package${testIdSuffix}-office`}>Office</MenuItem>
        <MenuItem value="Mailroom" data-testid={`package${testIdSuffix}-mailroom`}>Mailroom</MenuItem>
      </ValidatedDropdown>
    );

    const securityCodeField = (
      <ValidatedTextField
        hidden={hideDeliveryInfo}
        ref={securityCodeRef}
        optional
        id="security-code"
        label="Security Code"
        value={securityCode}
        onBlur={handleSecurityCodeValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(SECURITY_CODE, event); }}
        error={errors.securityCodeError}
        showErrors={showErrors}
        testId="security-code-input"
      />
    );

    const lockboxCodeField = (
      <ValidatedTextField
        hidden={hideDeliveryInfo}
        ref={lockboxCodeRef}
        optional
        id="lockbox-code"
        label="Lockbox Code"
        value={lockboxCode}
        onBlur={handleLockBoxCodeValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_CODE, event); }}
        error={errors.lockboxCodeError}
        showErrors={showErrors}
        testId="lockbox-code-input"
      />
    );

    const lockboxLocationField = (
      <ValidatedTextField
        hidden={hideDeliveryInfo}
        ref={lockboxLocationRef}
        optional
        id="lockbox-location"
        label="Lockbox Location"
        value={lockboxLocation}
        onBlur={handleLockBoxLocationValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(LOCKBOX_LOCATION, event); }}
        error={errors.lockboxLocationError}
        showErrors={showErrors}
        testId="lockbox-location-input"
      />
    );

    const propertyTypeSection = (
      <PropertyTypeSection
        hidden={hideDeliveryInfo}
        idSuffix={idSuffix}
        testIdSuffix={testIdSuffix}
        propertyType={propertyType}
        isPropertyTypeOther={isPropertyTypeOther}
        propertyTypeOther={propertyTypeOther}
        propertyTypeRef={propertyTypeRef}
        propertyTypeOtherRef={propertyTypeOtherRef}
        onPropertyTypeBlur={handlePropertyTypeValidation}
        onPropertyTypeChange={(event) => { handleDeliveryPreferencesOnChange(PROPERTY_TYPE_KEY, event); }}
        onPropertyTypeOtherBlur={handlePropertyTypeOtherValidation}
        onPropertyTypeOtherChange={(event) => { handleDeliveryPreferencesOnChange(PROPERTY_TYPE_OTHER_KEY, event); }}
        propertyTypeError={errors.propertyTypeError}
        propertyTypeOtherError={errors.propertyTypeOtherError}
        showErrors={showErrors}
      />
    );

    const hasStairsField = (
      <ValidatedDropdown
        hidden={hideDeliveryInfo}
        ref={hasStairsErrorRef}
        id={`has-stairs${idSuffix}`}
        testId={`has-stairs${testIdSuffix}-dropdown`}
        label={fieldLabels.hasStairs}
        value={getHasStairsDisplayValue(hasStairs)}
        onBlur={handleHasStairsValidation}
        onChange={(event) => { handleDeliveryPreferencesOnChange(HAS_STAIRS_KEY, event); }}
        placeholder="Select an option"
        error={errors.hasStairsError}
        showErrors={showErrors}
      >
        <MenuItem value="Yes" data-testid={`has-stairs${testIdSuffix}-yes`}>Yes</MenuItem>
        <MenuItem value="No" data-testid={`has-stairs${testIdSuffix}-no`}>No</MenuItem>
      </ValidatedDropdown>
    );

    const jobSiteHours = (
      <JobSiteHoursSection
        hidden={hideDeliveryInfo}
        idSuffix={idSuffix}
        testIdSuffix={testIdSuffix}
        jobSiteHoursStart={jobSiteHoursStart}
        jobSiteHoursEnd={jobSiteHoursEnd}
        startTimeOptions={startTimeOptions}
        endTimeOptions={endTimeOptions}
        jobSiteHoursStartRef={jobSiteHoursStartRef}
        jobSiteHoursEndRef={jobSiteHoursEndRef}
        onStartChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_START, event); }}
        onStartBlur={handleJobSiteHoursStartValidation}
        onEndChange={(event) => { handleDeliveryPreferencesOnChange(JOB_SITE_HOURS_END, event); }}
        onEndBlur={handleJobSiteHoursEndValidation}
        startError={errors.jobSiteHoursStartError}
        endError={errors.jobSiteHoursEndError}
      />
    );

    // Switched off: the pre-5168 accordion, fields in their original order, no section headings.
    // [DSCVR-5191]
    if (isAccordion && !isJobsiteInformationEnabled) {
      return (
        <>
          {projectTypeSection}
          {estimatedDates}
          {jobsiteConditionsField}
          {deliveryContactNameField}
          {deliveryContactNumberField}
          {deliveryInstructionsField}
          {deliveryTypeField}
          {packagePlacementField}
          {securityCodeField}
          {lockboxCodeField}
          {lockboxLocationField}
          {propertyTypeSection}
          {hasStairsField}
          {jobSiteHours}
        </>
      );
    }

    if (isAccordion) {
      // Project Type, Estimated Dates, Jobsite Conditions and Property Type are not shown in
      // any of the DSCVR-5168 comps. They stay on the legacy (switch-off) layout below - no
      // comp evidence either way for that surface - but the new layout hides them rather than
      // interrupting the four sections the comps do define.
      return (
        <>
          <SectionHeading title="Delivery Contact" helperText={JOBSITE_DELIVERY_CONTACT_HELPER_TEXT} />
          {deliveryContactNameField}
          {deliveryContactNumberField}
          <SectionHeading title="Delivery Time Window" helperText={ADDRESS_FORM_DELIVERY_TIME_WINDOW_HELPER_TEXT} />
          {jobSiteHours}
          <SectionHeading title="Delivery Instructions" helperText={ADDRESS_FORM_DELIVERY_INSTRUCTIONS_HELPER_TEXT} />
          {deliveryInstructionsField}
          {deliveryTypeField}
          {hasStairsField}
          {packagePlacementField}
          {securityCodeField}
          {lockboxLocationField}
          {lockboxCodeField}
        </>
      );
    }

    return (
      <>
        {projectTypeSection}
        {estimatedDates}
        {deliveryContactNameField}
        {deliveryContactNumberField}
        {jobsiteConditionsField}
        {deliveryInstructionsField}
        {packagePlacementField}
        {securityCodeField}
        {lockboxCodeField}
        {lockboxLocationField}
        {propertyTypeSection}
        {deliveryTypeField}
        {hasStairsField}
        {jobSiteHours}
      </>
    );
  };

  return (
    <>
      {!hideProjectInfo && (
        <TextField
          required
          ref={projectNameRef}
          id="project-name"
          label="Project Name"
          type="text"
          onBlur={handleNameValidation}
          value={projectName || ''}
          fullWidth
          onChange={(event) => { handleOnChange(PROJECT_NAME_KEY, event); }}
          disabled={hasTradeCreditAttached}
          status={
            errors.projectNameError || duplicateNameError ? 'error' : null
          }
          statusMessage={
            errors.projectNameError || duplicateNameError
          }
          InputProps={{
            inputProps: {
              'data-testid': 'project-name-input'
            }
          }}
        />
      )}

      {open ? (
        <>
          {renderFormFields(false)}
        </>
      ) : (
        <>
          {/* AC1: only the required Delivery Zip Code is surfaced above the accordion. The rest
              of the address moved inside it, so the drawer opens on two required fields rather
              than a full address block. */}
          {isJobsiteInformationEnabled && (
            <DeliveryZipField
              zipCode={jobsiteInformation?.zipCode}
              setJobsiteInformation={setJobsiteInformation}
              showErrors={showErrors}
              setIsDeliveryZipError={setIsDeliveryZipError}
            />
          )}
          <Accordion expanded={expandedAccordion} onChange={handleChangeAccordion}>
            <AccordionHeader aria-controls="accordion1-content" id="accordion1-header">
              <div className="sui-flex-auto sui-block">
                {/* DSCVR-5168: "(Optional)" is its own, lighter span - rendered as one string with the
            bold heading it inherited the same bold sui-text-primary weight/color, but the comp
            shows it lighter, matching the field-level (optional) tags rather than the heading
            next to it. The two pieces stay adjacent text in one element, so existing
            getByText(PROJECT_DELIVERY_DETAILS_TITLE) lookups on the combined string still
            resolve to this one header. */}
                <AccordionTitle isJobsiteInformationEnabled={isJobsiteInformationEnabled} />
              </div>
            </AccordionHeader>
            <AccordionBody>
              <div className="sui-grid sui-gap-6">
                {isJobsiteInformationEnabled && renderJobsiteSection(
                  'sui-pb-2', 'Address Details', ADDRESS_FORM_ADDRESS_DETAILS_HELPER_TEXT
                )}
                {renderFormFields(true)}
                {!isJobsiteInformationEnabled && renderJobsiteSection('sui-pt-8 sui-pb-4')}
              </div>
            </AccordionBody>
          </Accordion>
        </>
      )}
    </>
  );
};

// PropTypes and defaultProps remain the same...
ProjectInformationForm.propTypes = {
  setIsDeliveryZipError: func,
  showErrors: bool.isRequired,
  projectInformation: shape({
    projectName: string,
    projectType: string,
    otherPropertyType: string,
    estimatedStartDate: string,
    estimatedEndDate: string,
    notes: string
  }).isRequired,
  deliveryPreferences: shape({
    siteConditions: string,
    deliveryInstructions: string,
    preferredPackagePlacement: string,
    securityCode: string,
    lockboxCode: string,
    lockboxLocation: string,
    deliveryType: string,
    propertyType: string,
    hasStairs: bool,
    jobSiteHoursStart: string,
    jobSiteHoursEnd: string
  }).isRequired,
  contacts: shape({
    name: string,
    phone: string
  }).isRequired,
  setProjectInformation: func.isRequired,
  isProjectInfoError: bool.isRequired,
  setIsProjectInfoError: func.isRequired,
  open: bool.isRequired,
  duplicateNameError: string.isRequired,
  hasTradeCreditAttached: bool,
  setDeliveryPreferences: func.isRequired,
  setContacts: func.isRequired,
  jobsiteInformation: shape({
    addressLine1: string,
    addressLine2: string,
    city: string,
    state: string,
    zipCode: string,
    country: string
  }).isRequired,
  setJobsiteInformation: func.isRequired,
  isJobsiteError: bool.isRequired,
  setIsJobsiteError: func.isRequired,
  hideDeliveryInfo: bool,
  hideProjectInfo: bool,
  isJobsiteInformationEnabled: bool
};

ProjectInformationForm.defaultProps = {
  setIsDeliveryZipError: () => {},
  hasTradeCreditAttached: false,
  hideDeliveryInfo: false,
  hideProjectInfo: false,
  // Off means the pre-DSCVR-4956 layout: Jobsite Information stays inside the collapsed
  // "Project Details (Optional)" accordion instead of being surfaced above it.
  // CreateAProjectContent drives this from the fs:isJobsiteInformationEnabled switch.
  isJobsiteInformationEnabled: false
};

ProjectInformationForm.dataModel = extend(
  JobsiteInformationForm
);